import { Box, Typography } from '@mui/material';
import { ComponentProps, Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormikCheckbox,
  FormikSecretField as BaseFormikSecretField,
  FormikNumericField,
  FormikSelect,
  FormikTextField as BaseFormikTextField,
  HelpTooltip,
} from 'components';
import { P2PProviderType } from 'enums';
import { useActiveDynamicAdapterConfig } from 'features/dynamic-adapter-configs';
import { TranslationNamespace } from 'i18n';
import { hasPayinFlowMode } from 'utils/payin-flow-mode.utils';

import { PayinFlowModeSelect } from './PayinFlowModeSelect';

type Props = {
  path: string;
  isPayout: boolean;
  providerType?: P2PProviderType | string;
};

const KNOWN_ENUM_TYPES = new Set<string>(Object.values(P2PProviderType));

const FormikSecretField = (
  props: ComponentProps<typeof BaseFormikSecretField>,
) => <BaseFormikSecretField autoComplete="new-password" {...props} />;

const FormikTextField = (props: ComponentProps<typeof BaseFormikTextField>) => (
  <BaseFormikTextField autoComplete="off" {...props} />
);

export const P2PProviderCredentials: React.FC<Props> = ({
  path,
  isPayout,
  providerType,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });

  const isDynamicType = !!providerType && !KNOWN_ENUM_TYPES.has(providerType);
  const { config: dynamicConfig } = useActiveDynamicAdapterConfig(
    isDynamicType ? providerType : undefined,
  );

  const dynamicCredentialFields = useMemo(() => {
    if (!isDynamicType) {
      return [] as string[];
    }
    const fromPayin = dynamicConfig?.payinConfig?.requiredCredentials || [];
    const fromPayout = dynamicConfig?.payoutConfig?.requiredCredentials || [];
    return Array.from(new Set([...fromPayin, ...fromPayout]));
  }, [dynamicConfig, isDynamicType]);

  return (
    <Fragment>
      <Typography>{t('fields.credentials')}</Typography>
      {!isPayout && hasPayinFlowMode(providerType) && (
        <PayinFlowModeSelect
          path={path}
          allowOrderWithForm={providerType === P2PProviderType.Payscrow}
        />
      )}
      {isDynamicType && (
        <Fragment>
          {dynamicCredentialFields.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('fields.dynamic_credentials_hint')}
            </Typography>
          )}
          {dynamicCredentialFields.map((field) => (
            <FormikTextField
              key={field}
              label={field}
              name={`${path}.${field}`}
            />
          ))}
        </Fragment>
      )}
      {providerType === P2PProviderType.InternalLike && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.signature_key')}
            name={`${path}.signatureKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Trust2Pay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikSecretField
            label={t('fields.password')}
            name={`${path}.password`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.X2X && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikSecretField
            label={t('fields.password')}
            name={`${path}.password`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.Payscrow ||
        providerType === P2PProviderType.PayscrowMobile) && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Alaris && (
        <Fragment>
          <FormikTextField
            label={t('fields.project_id')}
            name={`${path}.projectId`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
            multiline
            rows={4}
          />
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.publicKey`}
            multiline
            rows={4}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.HighHelp && (
        <Fragment>
          <FormikTextField
            label={t('fields.project_id')}
            name={`${path}.projectId`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
            multiline
            rows={4}
          />
          <FormikTextField
            label={t('fields.request_hmac_key')}
            name={`${path}.hmacKey`}
          />
          <FormikTextField
            label={t('fields.webhook_public_key')}
            name={`${path}.webhookPublicKey`}
            multiline
            rows={4}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookHmacKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Namba && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.NambaOne ||
        providerType === P2PProviderType.Npay) && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.secret`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantAccountGuid`}
          />
          <Box display="flex" alignItems="center">
            <FormikTextField
              label={t('fields.create_api_version')}
              name={`${path}.createApiVersion`}
            />
            <HelpTooltip title={t('fields.create_api_version_description')} />
          </Box>
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.NambaHyperion && (
        <Fragment>
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikTextField
            label={t('fields.password')}
            name={`${path}.password`}
          />
          <FormikTextField label={t('fields.dealer')} name={`${path}.dealer`} />
          <FormikTextField
            label={t('fields.default_bearer')}
            name={`${path}.defaultBearer`}
          />
          <FormikCheckbox
            label={t('fields.exclude_commission')}
            name={`${path}.excludeCommission`}
          />
          <FormikCheckbox
            label={t('fields.keep_plus')}
            name={`${path}.keepPlus`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Noros && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Duna && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
          <FormikTextField
            label={t('fields.ca_cert')}
            name={`${path}.caCert`}
            multiline
            rows={4}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Paycraft && (
        <Fragment>
          <FormikTextField
            label={t('fields.client_code_name')}
            name={`${path}.clientCodeName`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PaycraftElqr && (
        <Fragment>
          <FormikTextField
            label={t('fields.point_id')}
            name={`${path}.pointId`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
            multiline
            rows={4}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PaycraftV1_1 && (
        <Fragment>
          <FormikTextField
            label={t('fields.client_id')}
            name={`${path}.clientId`}
          />
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikSecretField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Exwave && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.host2host')}
            name={`${path}.host2host`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Reactivepay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Realpay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.shop_id')}
            name={`${path}.shopId`}
          />
          <FormikCheckbox label={t('fields.is_ftd')} name={`${path}.isFtd`} />
        </Fragment>
      )}
      {providerType === P2PProviderType.Mapsign && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.basic_auth')}
            name={`${path}.basicAuth`}
          />
          <FormikSecretField
            label={t('fields.bearer_token')}
            name={`${path}.bearerToken`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.Titanpay ||
        providerType === P2PProviderType.TitanpayAlfa) && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Fintrix && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikSecretField label={t('fields.salt')} name={`${path}.salt`} />
        </Fragment>
      )}
      {providerType === P2PProviderType.Payfinity && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.publicKey`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.XPayments && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.contract_id')}
            name={`${path}.contractId`}
          />
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.MoneyMania && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.webhookPublicKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.MoneyHub && (
        <Fragment>
          <FormikSecretField
            label={t('fields.access_token_payin')}
            name={`${path}.accessTokenPayin`}
          />
          <FormikSecretField
            label={t('fields.access_token_payout')}
            name={`${path}.accessTokenPayout`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.IDMPay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.customer_uuid')}
            name={`${path}.customerUUID`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.P2PTrade && (
        <Fragment>
          <FormikTextField
            label={t('fields.client_id')}
            name={`${path}.clientId`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.Bridgepay ||
        providerType === P2PProviderType.Beikra) && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikCheckbox
            label={t('fields.force_invoice_url')}
            name={`${path}.forceInvoiceUrl`}
          />
          <FormikNumericField
            label={t('fields.randomization_max_amount_delta')}
            name={`${path}.maxAmount`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.OnlyOnePays && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.default_bank_id')}
            name={`${path}.defaultBankId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Mercurio && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Airpay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Flackopay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikSecretField
            label={t('fields.api_token_payout_sbp')}
            name={`${path}.apiTokenPayoutSbp`}
          />
          <FormikSecretField
            label={t('fields.api_token_payout_card')}
            name={`${path}.apiTokenPayoutCard`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Garex && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.token`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.GiPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.NoName && (
        <Fragment>
          <FormikSecretField
            label={t('fields.auth_token')}
            name={`${path}.authToken`}
          />
          <FormikSecretField
            label={t('fields.signature_key')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Pentax && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BBRPay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Kingsman && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.account_id')}
            name={`${path}.accountId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Panda && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Pay2Play && (
        <Fragment>
          <FormikTextField
            label={t('fields.refresh_token')}
            name={`${path}.refreshToken`}
          />
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.webhook_public_key')}
            name={`${path}.webhookPublicKey`}
            multiline
            rows={4}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.ManyPay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayCrown && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayPlace && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BeePay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Gotham && (
        <Fragment>
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Pulsar && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikSelect
            label={t('fields.webhook_amount_field')}
            name={`${path}.webhookAmountField`}
            options={[
              { value: 'amount', label: 'amount' },
              { value: 'init_amount', label: 'init_amount' },
            ]}
          />
          <FormikCheckbox
            label={t('fields.require_webhook_signature')}
            name={`${path}.requireWebhookSignature`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.BNPay ||
        providerType === P2PProviderType.SwipePay ||
        providerType === P2PProviderType.SwipePayV1) && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.shop_id')}
            name={`${path}.shopId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Montra && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayMatrix && (
        <Fragment>
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikSecretField
            label={t('fields.otp_secret')}
            name={`${path}.otpSecret`}
          />
          <FormikTextField
            label={t('fields.sso_url')}
            name={`${path}.ssoUrl`}
          />
          <FormikTextField
            label={t('fields.payment_form_merchant')}
            name={`${path}.paymentFormMerchant`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.TrustPay && (
        <Fragment>
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.TrustCloud && (
        <Fragment>
          <FormikSecretField
            label={t('fields.casino_hash')}
            name={`${path}.casinoHash`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.EHotPay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.EpayBrl ||
        providerType === P2PProviderType.EpayV2) && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField label={t('fields.app_id')} name={`${path}.appId`} />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
          {!isPayout && providerType === P2PProviderType.EpayV2 && (
            <FormikTextField
              label={t('fields.default_pay_method')}
              name={`${path}.defaultPayMethod`}
            />
          )}
        </Fragment>
      )}
      {providerType === P2PProviderType.Gopago && (
        <Fragment>
          <FormikTextField label={t('fields.app_id')} name={`${path}.appId`} />
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.Profitable ||
        providerType === P2PProviderType.ProfitableLight) && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Cactus && (
        <Fragment>
          <FormikTextField
            label={t('fields.site_id')}
            name={`${path}.siteId`}
          />
          <FormikTextField
            label={t('fields.site_login')}
            name={`${path}.siteLogin`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikSecretField label={t('fields.salt')} name={`${path}.salt`} />
        </Fragment>
      )}
      {providerType === P2PProviderType.Arhitec && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.X10Processing && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Europays && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Astrum && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.fernet_key')}
            name={`${path}.fernetKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Coince && (
        <Fragment>
          <FormikSecretField
            label={t('fields.access_token')}
            name={`${path}.accessToken`}
          />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.default_payout_gateway')}
            name={`${path}.defaultPayoutGateway`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BitWire && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikSecretField
            label={t('fields.password')}
            name={`${path}.password`}
          />
          <FormikTextField
            label={t('fields.account_id')}
            name={`${path}.accountId`}
          />
          <FormikCheckbox
            label={t('fields.send_customer_data')}
            name={`${path}.sendCustomerData`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Blackberry && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BlackStar && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Stbl && (
        <Fragment>
          <FormikTextField
            label={t('fields.username')}
            name={`${path}.username`}
          />
          <FormikSecretField
            label={t('fields.password')}
            name={`${path}.password`}
          />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.Bova ||
        providerType === P2PProviderType.BovaMini) && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.user_id')}
            name={`${path}.userId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Brusnikapay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.webhook_public_key')}
            name={`${path}.webhookPublicKey`}
            multiline
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PumP2P && (
        <Fragment>
          <FormikSecretField
            label={t('fields.auth_token')}
            name={`${path}.authToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.RapidPays && (
        <Fragment>
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.FreeFlow && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.shop_id')}
            name={`${path}.shopId`}
          />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secret1`}
          />
          <FormikSecretField
            label={t('fields.webhook_secret')}
            name={`${path}.secret2`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Forta && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Incas && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikSelect
            label={t('fields.webhook_amount_field')}
            name={`${path}.webhookAmountField`}
            options={[
              { value: 'amount', label: 'amount' },
              { value: 'amountSource', label: 'amountSource' },
            ]}
          />
          <FormikTextField
            label={t('fields.default_bank')}
            name={`${path}.defaultPayinBank`}
          />
          <FormikCheckbox
            label={t('fields.require_webhook_signature')}
            name={`${path}.requireWebhookSignature`}
          />
        </Fragment>
      )}
      {(providerType === P2PProviderType.IronPay ||
        providerType === P2PProviderType.TestProvider) && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.InfinityPay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.merchant_token')}
            name={`${path}.merchantToken`}
          />
          <FormikSecretField
            label={t('fields.merchant_secret')}
            name={`${path}.merchantSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.HiTrust && (
        <FormikTextField label={t('fields.api_key')} name={`${path}.apiKey`} />
      )}
      {providerType === P2PProviderType.PaymentLegacy && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Jele && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSelect
            label={t('fields.traffic_type')}
            name={`${path}.trafficType`}
            options={[
              { value: 'ftd', label: 'FTD' },
              { value: 'std', label: 'STD' },
            ]}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.JustMoney && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Kazex && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.callbackSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Jakarta && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
          <FormikTextField
            label={t('fields.default_bank')}
            name={`${path}.defaultPayinBank`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Almaty && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Alpina && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.webhook_secret')}
            name={`${path}.callbackSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Argos && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BayPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BBBPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Tokyo && (
        <Fragment>
          <FormikSecretField
            label={t('fields.bearer_token')}
            name={`${path}.bearerToken`}
          />
          <FormikSecretField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
          {!isPayout && (
            <FormikTextField
              label={t('fields.widget_id')}
              name={`${path}.widgetId`}
            />
          )}
        </Fragment>
      )}
      {providerType === P2PProviderType.Expay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.apiPublic`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.apiPrivate`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.ExpayOne && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField
            label={t('fields.access_token')}
            name={`${path}.accessToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.ProFiat && (
        <Fragment>
          <FormikTextField label={t('fields.uid')} name={`${path}.uid`} />
          <FormikTextField
            label={t('fields.shop_id')}
            name={`${path}.shopId`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Kvell && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikSecretField
            label={t('fields.signature_key')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.LazyTrade && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.LionTech && (
        <Fragment>
          <FormikSecretField
            label={t('fields.access_token')}
            name={`${path}.accessToken`}
          />
          <FormikSecretField
            label={t('fields.refresh_token')}
            name={`${path}.refreshToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.SkyPhoenix && (
        <Fragment>
          <FormikTextField
            label={t('fields.token_public')}
            name={`${path}.tokenPublic`}
          />
          <FormikSecretField
            label={t('fields.token_private')}
            name={`${path}.tokenPrivate`}
          />
          <FormikTextField label={t('fields.email')} name={`${path}.email`} />
        </Fragment>
      )}
      {providerType === P2PProviderType.SBPay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.FastPay && (
        <Fragment>
          <FormikSecretField label={t('fields.token')} name={`${path}.token`} />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Havalepayhera && (
        <Fragment>
          <FormikSecretField label={t('fields.api_key')} name={`${path}.key`} />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secret`}
          />
          <FormikTextField
            label={t('fields.default_bank')}
            name={`${path}.defaultBank`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.FlowPay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Hawaii && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.RoyalFinance && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookStaticToken`}
          />
          {!isPayout && (
            <FormikCheckbox
              label={t('fields.require_webhook_signature')}
              name={`${path}.requireWebhookSignature`}
            />
          )}
        </Fragment>
      )}
      {providerType === P2PProviderType.Samarsky && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Aggregato && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Affinex && (
        <Fragment>
          <FormikTextField
            label={t('fields.username')}
            name={`${path}.username`}
          />
          <FormikSecretField
            label={t('fields.password')}
            name={`${path}.password`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.SevenXPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayUp && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_public')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Unite && (
        <Fragment>
          <FormikTextField label={t('fields.api_key')} name={`${path}.keyId`} />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.serverPublicKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Vexora && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantNo`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Vienna && (
        <Fragment>
          <FormikTextField
            label={t('fields.terminal_id')}
            name={`${path}.terminalId`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
          <FormikTextField
            label={t('fields.polling_url')}
            name={`${path}.pollingUrl`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayVia && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Ampay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.apiPublic`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.apiPrivate`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.TokenRanch && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.TradeSide && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.publicKey`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.ReactPay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Rounin && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Paylord && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikTextField label={t('fields.login')} name={`${path}.login`} />
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayGateCore && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.merchantToken`}
          />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Decard && (
        <Fragment>
          <FormikSecretField
            label={t('fields.shop_key')}
            name={`${path}.shopKey`}
          />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Easy2Tap && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikTextField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Payshark && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikSecretField
            label={t('fields.access_token')}
            name={`${path}.accessToken`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.TeleStore && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.publicKey`}
          />
          <FormikSecretField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
          <FormikTextField label={t('fields.app_id')} name={`${path}.appId`} />
        </Fragment>
      )}
      {providerType === P2PProviderType.Tenet && (
        <Fragment>
          <FormikTextField
            label={t('fields.username')}
            name={`${path}.username`}
          />
          <FormikSecretField
            label={t('fields.password')}
            name={`${path}.password`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.EasyTokenPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.username')}
            name={`${path}.username`}
          />
          <FormikTextField
            label={t('fields.password')}
            name={`${path}.password`}
          />
          <FormikTextField
            label={t('fields.secret_key')}
            name={`${path}.callbackSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Piqpay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PrMoney && (
        <Fragment>
          <FormikTextField
            label={t('fields.client_id')}
            name={`${path}.clientId`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.clientSecret`}
          />
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.token`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.WorldPay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BillionPay && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Transavex && (
        <Fragment>
          <FormikTextField
            label={t('fields.client_id')}
            name={`${path}.clientId`}
          />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.BucksBunny && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.xSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Vnd && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.LuxCore && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Meridian && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Mesa && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.bearer_token')}
            name={`${path}.bearerToken`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.secret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.ScroogePay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.partnerId`}
          />
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Sequoia && (
        <Fragment>
          <FormikSecretField
            label={t('fields.secret_key')}
            name={`${path}.secretKey`}
          />
          <FormikSecretField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.YummyPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchant`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.privateToken`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.MacPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantNo`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
            multiline
            rows={4}
          />
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.platformPublicKey`}
            multiline
            rows={4}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Nodex && (
        <Fragment>
          <FormikTextField
            label={t('fields.nodex_shop_id')}
            name={`${path}.shopId`}
            required
          />
          <FormikTextField
            label={t('fields.merchant_key_id')}
            name={`${path}.keyId`}
            required
          />
          <Box display="flex" alignItems="center">
            <FormikTextField
              label={t('fields.platform_key_id')}
              name={`${path}.platformKeyId`}
              fullWidth
            />
            <HelpTooltip title={t('fields.platform_key_id_description')} />
          </Box>
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
            required
            multiline
            rows={4}
          />
          <FormikTextField
            label={t('fields.webhook_public_key')}
            name={`${path}.webhookPublicKey`}
            required
            multiline
            rows={4}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Zentra && (
        <Fragment>
          <FormikTextField
            label={t('fields.merchant_id')}
            name={`${path}.merchantId`}
          />
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikSecretField
            label={t('fields.api_secret')}
            name={`${path}.apiSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Fenix && (
        <Fragment>
          <FormikTextField label="Client ID" name={`${path}.clientId`} />
          <FormikSecretField
            label="Client Secret"
            name={`${path}.clientSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Finex && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.RiskPay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.PayfinityV2 && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_public')}
            name={`${path}.publicKey`}
          />
          <FormikTextField
            label={t('fields.api_private')}
            name={`${path}.privateKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Psp24 && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
          <FormikTextField
            label={t('fields.webhook_secret')}
            name={`${path}.webhookSecret`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Edro && (
        <Fragment>
          <FormikSecretField
            label={t('fields.api_key')}
            name={`${path}.apiKey`}
          />
        </Fragment>
      )}
      {providerType === P2PProviderType.Upay && (
        <Fragment>
          <FormikTextField
            label={t('fields.api_token')}
            name={`${path}.apiToken`}
          />
        </Fragment>
      )}
    </Fragment>
  );
};
